import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { LinearProgress } from '@mui/material';
import { toFixedTrunc, useJunoCommonFunctions } from '../../../client/Juno/helpers';
import { useSelector } from 'react-redux';
import { currencyColors } from '../../helpers/constant';
import ActionBar from '../ActionBar';
import OverviewSummaryForMobileView from './OverviewSummaryForMobileView';

const OverviewSummary = ({ balancesData, totalAmount }) => {
    const imageBaseUrl = useSelector(state => state.config.image_base_url);
    const activeCurrencyPage = useSelector(state => state.juno.activeCurrencyPage);
    const [transformedBalances, setTransformedBalances] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const { useWindowWidth } = useJunoCommonFunctions();
    const windowWidth = useWindowWidth();

    const transformBalances = () => {
        // Sort the array based on holdingPercentage in  order
        const sortedData = balancesData
            ?.filter(balance => balance.currencyShortName != 'TRC20-USDT')
            .sort((a, b) => b.holdingPercentage - a.holdingPercentage);
        const top6 = sortedData?.slice(0, 6);

        // Create a new array with top 6 currencies
        const result = top6?.map((item, index) => ({
            currency: item.currencyShortName,
            holdingPercentage: item.holdingPercentage || 0,
            color: currencyColors.find(colorItem => colorItem?.symbol === item?.currencyShortName)
                ?.color,
            icon: item.currencyIcon,
            balance: item.balanceAmount,
        }));

        setTransformedBalances(result.every(item => !item.balance) ? [] : result);
    };

    useEffect(() => {
        transformBalances();
    }, [balancesData, totalAmount]);

    useEffect(() => {
        const observer = new MutationObserver(() => {
            const textElement = document.querySelector('.apexcharts-datalabel-label');

            if (textElement && !textElement.querySelector('tspan')) {
                const textContent = textElement.textContent;
                if (!textContent) return;

                const firstLetter = textContent.charAt(0);
                const remainingText = textContent.slice(1);
                textElement.innerHTML = `
                    <tspan class="first-letter">${firstLetter}</tspan>${remainingText}
                `;
            }

            const datalabelGroup = document.querySelector('.apexcharts-datalabels-group');
            if (datalabelGroup) {
                const textElements = datalabelGroup.querySelectorAll('text');
                if (textElements.length > 1) {
                    textElements[1].remove();
                }
            }
        });

        const target = document.querySelector('.apexcharts-datalabel-label') || document.body;

        observer.observe(target, {
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();
        };
    }, []);

    const chartOptions = {
        chart: {
            type: 'donut',
            id: 'overviewchart',
            fontFamily: 'Inter, sans-serif',
        },
        colors: transformedBalances?.map(item => item.color) || [],
        plotOptions: {
            pie: {
                donut: {
                    size: '94%',
                    labels: {
                        show: true,
                        name: {
                            offsetY: 10,
                        },
                        total: {
                            show: true,
                            showAlways: true,
                            label: `$${toFixedTrunc(totalAmount, 2)}`,
                        },
                    },
                },
            },
        },
        stroke: {
            show: false,
        },
        labels: transformedBalances?.map(item => item.currency) || [],
        dataLabels: {
            enabled: false,
        },
        legend: {
            show: false,
        },
        responsive: [
            {
                breakpoint: 480,
                options: {
                    chart: {
                        width: 300,
                    },
                    legend: {
                        position: 'bottom',
                    },
                },
            },
            {
                // 'Fake' breakpoint that will never apply -- workaround to prevent apexchart from breaking
                breakpoint: 9001,
                options: {},
            },
        ],
    };

    const chartSeries = transformedBalances?.map(item => item.holdingPercentage) || [];
    return (
        <div className="overview-container h-full border border-[#0000001F] dark:!border-[#FFFFFF1F] lg:rounded-lg">
            <div className="overview-bg h-full bg-cover bg-center flex justify-center items-center self-stretch relative lg:rounded-lg outline outline-1 outline-offset-[-1px] outline-white/10 overflow-visible m-0 lg:m-[2px]">
                <div data-e2e-total-amount="amount-value" data-e2e={totalAmount}className="donut-chart flex flex-col lg:flex-row gap-[44px] lg:gap-[90px] w-[723px] flex-1 justify-center items-center mt-14 lg:mt-0">
                    {chartSeries.length && transformedBalances.length ? (
                        <Chart
                            options={chartOptions}
                            series={chartSeries}
                            type="donut"
                            width="350"
                            onClick={() => setOpenModal(true)}
                        />
                    ) : (
                        <div className="w-72 h-72 rounded-full border-8 border-[#E4E4E7] dark:border-[#70707B] flex items-center justify-center">
                            <div className="text-text font-['Inter']">
                                <span className="text-2xl font-medium leading-normal">$</span>
                                <span className="text-4xl font-semibold leading-10">0</span>
                            </div>
                        </div>
                    )}
                    {transformedBalances?.length ? (
                        <div className="w-[327px] hidden lg:block">
                            <div className="flex justify-between flex-col gap-4">
                                {transformedBalances?.map((item, index) => {
                                    return (
                                        <div className="flex justify-between" key={index}>
                                            <div className="flex justify-center items-center gap-3">
                                                <img
                                                    src={`${imageBaseUrl}${item?.icon}`}
                                                    className="w-6 h-6"
                                                    alt="currencyIcon"
                                                />
                                                <span className="text-sm font-medium text-text">
                                                    {item?.currency}
                                                </span>
                                            </div>

                                            <div className="w-32 flex flex-col">
                                                <p className="ml-auto !text-text">
                                                    {toFixedTrunc(item?.holdingPercentage, 2)}%
                                                </p>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={item?.holdingPercentage}
                                                    sx={{
                                                        '&&': {
                                                            backgroundColor: 'var(--progress-bg)',
                                                            transform: 'rotateY(180deg)',
                                                            '& .MuiLinearProgress-bar': {
                                                                transform: 'rotateY(180deg)',
                                                                backgroundColor:
                                                                    currencyColors.find(
                                                                        colorItem => {
                                                                            return (
                                                                                colorItem?.symbol ===
                                                                                item?.currency
                                                                            );
                                                                        },
                                                                    )?.color,
                                                            },
                                                        },
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}
                    <div className="lg:hidden block w-full">
                        <ActionBar />
                    </div>
                </div>
            </div>

            {/* For mobile and tablet view only */}
            {windowWidth <= 1024 && (
                <OverviewSummaryForMobileView
                    transformedBalances={transformedBalances}
                    totalAmount={totalAmount}
                    openModal={openModal}
                    setOpenModal={setOpenModal}
                />
            )}
        </div>
    );
};

export default OverviewSummary;
